import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';

// Supported themes (independently named aesthetics)
const DEFAULT_THEME = 'default';
const THEME_LIST = [DEFAULT_THEME, 'neo', 'mint', 'sky', 'frozen', 'jade', 'magenta', 'autumn', 'vice'];

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  themes: THEME_LIST,
});

export const ThemeProvider = ({ children }) => {
  const { userProfile, loading } = useAuth();
  const isAuthenticated = !!userProfile;

  // internalTheme holds the user's preference from localStorage.
  const [internalTheme, setInternalTheme] = useState(() => {
    let stored = localStorage.getItem('theme');
    // Legacy theme migration for the rename of retro80s -> vice
    if (stored === 'retro80s') {
      stored = 'vice';
      localStorage.setItem('theme', 'vice');
    }
    return THEME_LIST.includes(stored) ? stored : DEFAULT_THEME;
  });

  // The effective theme depends on authentication status.
  // It's default if loading or not authenticated.
  const theme = !loading && isAuthenticated ? internalTheme : DEFAULT_THEME;

  // Effect to apply the effective theme to the document.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const setTheme = useMemo(
    () => (next) => {
      // Only allow authenticated users to change their preferred theme.
      if (!isAuthenticated || loading) {
        return;
      }

      const final = THEME_LIST.includes(next) ? next : DEFAULT_THEME;
      localStorage.setItem('theme', final);
      setInternalTheme(final);
    },
    [isAuthenticated, loading]
  );

  const value = useMemo(() => ({ theme, setTheme, themes: THEME_LIST }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
